import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  SettingsContext,
  SettingsContextType,
  ThemeKey,
} from './SettingsContext';
import { ThemeProvider } from '@mui/material/styles';
import { ReemaThemeV2 } from '../reemaThemeV2';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { appSettingsRequests } from 'redux/requests/app-settings.requests';
import {
  CustomizationThemeDto,
  UserAppSettingsDto,
} from '@act/shared/data-transfer-objects';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice.slice';

interface SettingsProviderProps {
  children: React.ReactNode;
}

interface LocalThemeSettings {
  themeKey: ThemeKey;
}

const defaultThemeSettings: LocalThemeSettings = {
  themeKey: 'LIGHT_MODE',
};

export const SettingsProvider = ({ children }: SettingsProviderProps) => {
  const [currentThemeKey, setCurrentThemeKey] = useState<ThemeKey | null>(null);

  // Get current user
  const actingUser = actingUserAppSelectors.useSelectActingUser();

  // Fetch customization themes
  const [
    customizationThemesResponse,
    isLoadingThemes,
    fetchCustomizationThemes,
  ] = useApiCall(appSettingsRequests.getAllCustomizationThemes);

  const customizationThemes = useMemo<CustomizationThemeDto[]>(
    () => customizationThemesResponse || [],
    [customizationThemesResponse]
  );

  // Fetch user app settings
  const [userAppSettingsResponse, isLoadingUserSettings, fetchUserAppSettings] =
    useApiCall(appSettingsRequests.getUserAppSettings);

  // Update user app settings
  const [updateResult, isUpdatingSettings, updateSettings] = useApiCall(
    appSettingsRequests.updateUserAppSettings
  );

  const userAppSettings = useMemo<UserAppSettingsDto | null>(
    () => updateResult || userAppSettingsResponse || null,
    [updateResult, userAppSettingsResponse]
  );

  // Extract individual IDs to prevent unnecessary re-renders
  const logoThemeId = userAppSettings?.logoThemeId ?? null;
  const accentThemeId = userAppSettings?.accentThemeId ?? null;
  const showAnimations = userAppSettings?.showAnimations ?? false;

  const saveThemeSettings = useCallback((settings: LocalThemeSettings) => {
    try {
      localStorage.setItem('themeSettings', JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save theme settings to localStorage:', error);
    }
  }, []);

  useEffect(() => {
    if (actingUser?.id) {
      fetchCustomizationThemes({ params: null, body: null });
      fetchUserAppSettings({ params: { userId: actingUser.id }, body: null });
    }
  }, [actingUser?.id]);

  // Load theme preference from localStorage
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem('themeSettings');
      let loadedSettings: LocalThemeSettings;

      if (savedSettings === null) {
        loadedSettings = defaultThemeSettings;
        saveThemeSettings(defaultThemeSettings);
      } else {
        loadedSettings = JSON.parse(savedSettings) as LocalThemeSettings;
        loadedSettings.themeKey =
          (Object.keys(ReemaThemeV2).includes(loadedSettings.themeKey) &&
            loadedSettings.themeKey) ||
          defaultThemeSettings.themeKey;
      }
      setCurrentThemeKey(loadedSettings.themeKey);
    } catch (error) {
      console.error(
        'Failed to load or parse theme settings from localStorage:',
        error
      );
      setCurrentThemeKey(defaultThemeSettings.themeKey);
      saveThemeSettings(defaultThemeSettings);
    }
  }, [saveThemeSettings]);

  const actualThemeKey = currentThemeKey ?? defaultThemeSettings.themeKey;

  const setThemeKey = useCallback(
    (newThemeKey: ThemeKey) => {
      if (Object.keys(ReemaThemeV2).includes(newThemeKey)) {
        setCurrentThemeKey(newThemeKey);
        saveThemeSettings({
          themeKey: newThemeKey,
        });
      } else {
        console.warn(`Attempted to set unknown theme key: ${newThemeKey}`);
      }
    },
    [saveThemeSettings]
  );

  // Compute gradient colors from themes using the IDs
  const logoGradientColors = useMemo(() => {
    if (!logoThemeId || customizationThemes.length === 0) {
      return [];
    }
    const theme = customizationThemes.find(t => t.id === logoThemeId);
    return theme?.logoGradientColors || [];
  }, [customizationThemes, logoThemeId]);

  const accentGradientColors = useMemo(() => {
    if (!accentThemeId || customizationThemes.length === 0) {
      return [];
    }
    const theme = customizationThemes.find(t => t.id === accentThemeId);
    return theme?.accentGradientColors || [];
  }, [customizationThemes, accentThemeId]);

  // Function to update user app settings and refetch
  const updateUserAppSettings = useCallback(
    async (settings: {
      logoThemeId?: number | null;
      accentThemeId?: number | null;
      showAnimations?: boolean;
    }) => {
      if (!actingUser?.id) {
        console.warn('No user ID available to update settings');
        return;
      }

      await updateSettings({
        params: { userId: actingUser.id },
        body: settings,
      });
    },
    [actingUser?.id, updateSettings]
  );

  const muiTheme = useMemo(() => {
    if (actualThemeKey) {
      return ReemaThemeV2[actualThemeKey];
    }
    return ReemaThemeV2[defaultThemeSettings.themeKey];
  }, [actualThemeKey]);

  const contextValue: SettingsContextType = useMemo(
    () => ({
      theme: muiTheme,
      themeKey: actualThemeKey,
      setThemeKey,
      logoThemeId,
      accentThemeId,
      logoGradientColors,
      accentGradientColors,
      showAnimations,
      customizationThemes,
      isLoadingThemes,
      userAppSettings,
      isLoadingUserSettings: isLoadingUserSettings || isUpdatingSettings,
      updateUserAppSettings,
    }),
    [
      muiTheme,
      actualThemeKey,
      setThemeKey,
      logoThemeId,
      accentThemeId,
      logoGradientColors,
      accentGradientColors,
      showAnimations,
      customizationThemes,
      isLoadingThemes,
      userAppSettings,
      isLoadingUserSettings,
      isUpdatingSettings,
      updateUserAppSettings,
    ]
  );

  if (currentThemeKey === null) {
    return null;
  }

  return (
    <SettingsContext.Provider value={contextValue}>
      <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
    </SettingsContext.Provider>
  );
};
